import { useAuthV2, type DataVisibility } from "./store";
import { ACTION, type Action } from "@/lib/auth/actions";

/**
 * Checks if the current user has the given action permission.
 * Non-reactive read — use inside event handlers, sagas, or non-React code.
 * Inside components, prefer `usePermission(action)` so the component re-renders
 * when the session changes (login / logout / refresh).
 */
export function hasPermission(action: Action | string): boolean {
  return useAuthV2.getState().permissions.has(action);
}

/**
 * Reactive hook — re-renders the calling component when the permissions
 * Set changes. Returns the full Set so callers can do multi-check logic.
 */
export function usePermissions(): Set<string> {
  return useAuthV2((s) => s.permissions);
}

/**
 * Convenience hook for single-action boolean check. Re-renders when the
 * answer flips (login / logout). Prefer over `hasPermission` inside
 * components.
 *
 * @example
 *   const canDelete = usePermission(ACTION.OrderDelete);
 *   {canDelete && <DeleteButton />}
 */
export function usePermission(action: Action | string): boolean {
  return useAuthV2((s) => s.permissions.has(action));
}

const FULLY_VISIBLE: DataVisibility = { orderMoney: true, customerContact: true };

/**
 * Cờ hiển thị tiền / liên lạc khách của người đang xem (QĐ 0132, 12/09/2026).
 * Trang dùng nó để ẨN Ô thay vì vẽ "0 đ" và ô trống — máy chủ đã che sẵn,
 * đây chỉ là chuyện màn hình nói cho thật.
 *
 * Chưa có cờ (vé cũ, vừa tải trang, đang chờ `/auth/refresh`, hay bản giả
 * store trong bài kiểm) thì coi như CÒN THẤY: thà hiện số của máy chủ một nhịp
 * còn hơn giấu nhầm ô của lãnh đạo — máy chủ vẫn che với người không được xem.
 * Đọc thẳng `s.dataVisibility` chứ không qua một export khác của store: bản
 * giả store thiếu export là vitest ném lỗi ở mọi màn hình gọi hook này.
 *
 * @example
 *   const { orderMoney, customerContact } = useDataVisibility();
 *   {orderMoney && <MoneyBlock order={order} />}
 */
export function useDataVisibility(): DataVisibility {
  return useAuthV2((s) => s.dataVisibility ?? FULLY_VISIBLE);
}

/**
 * Departments whose plain staff may create/edit orders without holding the
 * `order.write` permission. Managers/coordinators in these departments already
 * get the capability via their role, so this fallback only covers role=staff.
 * Mirrors the backend `AllowOrDept("order.write", "reception", "marketing")`
 * gate so the FE entry points match server authorization.
 */
const ORDER_WRITE_STAFF_DEPTS = new Set(["reception", "marketing"]);

/**
 * Whether the current user may create/edit orders. True when they hold the
 * `order.write` permission OR they are staff in an order-write department
 * (reception, marketing). Non-reactive read — use in event handlers /
 * non-React code.
 */
export function canCreateOrder(): boolean {
  const s = useAuthV2.getState();
  if (s.permissions.has(ACTION.OrderWrite)) return true;
  const staff = s.currentStaff;
  return !!staff && staff.role === "staff" && ORDER_WRITE_STAFF_DEPTS.has(staff.department);
}

/**
 * Reactive variant of canCreateOrder — re-renders the calling component when
 * the session changes (login / logout / refresh).
 */
export function useCanCreateOrder(): boolean {
  return useAuthV2(
    (s) =>
      s.permissions.has(ACTION.OrderWrite) ||
      (!!s.currentStaff && s.currentStaff.role === "staff" && ORDER_WRITE_STAFF_DEPTS.has(s.currentStaff.department)),
  );
}
